/**
 * TikTok Pixel tracking utility
 * - Production-only: Events only fire in production
 * - SSR-safe: All functions check for window/document
 */

/**
 * Validate email before sending to tracking pixels.
 * Rejects empty, malformed, and known placeholder values.
 */
export function isValidTrackingEmail(email: string): boolean {
    const trimmed = email.toLowerCase().trim()
    if (!trimmed || !trimmed.includes("@")) return false
    const blocklist = ["null", "undefined", "redacted", "test@example.com", "mail@example.com", "demo@outfame.com"]
    if (blocklist.includes(trimmed)) return false
    // Basic format: something@something.something
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return false
    return true
}

export interface TikTokEventParams {
    content_name?: string
    content_category?: string
    content_id?: string
    content_type?: string
    value?: number
    currency?: string
    status?: string
    [key: string]: string | number | undefined
}

export interface TikTokAttributionData {
    ttp: string | null
    ttclid: string | null
}

/**
 * Track a TikTok Pixel event
 * @param eventName - Event name (e.g., 'CompleteRegistration', 'ViewContent', 'Purchase')
 * @param params - Event parameters
 * @param eventId - Optional event ID for deduplication with server-side events
 */
export function trackTikTokEvent(
    eventName: string,
    params?: TikTokEventParams,
    eventId?: string
): void {
    if (process.env.NODE_ENV !== "production") return
    if (typeof window === "undefined") return

    const ttq = (window as any).ttq
    if (!ttq) {
        console.warn("[TikTok Pixel] ttq not loaded")
        return
    }

    try {
        if (eventId) {
            ttq.track(eventName, params || {}, { event_id: eventId })
        } else {
            ttq.track(eventName, params || {})
        }
    } catch (error) {
        console.error("[TikTok Pixel] Error tracking event:", error)
    }
}

/**
 * Generate a deterministic event ID for deduplication
 * Format: {prefix}_{identifier}_{YYYYMMDD}
 */
export function generateTikTokEventId(prefix: string, identifier: string): string {
    const now = new Date()
    const year = now.getUTCFullYear()
    const month = String(now.getUTCMonth() + 1).padStart(2, "0")
    const day = String(now.getUTCDate()).padStart(2, "0")
    return `${prefix}_${identifier}_${year}${month}${day}`
}

/**
 * Set TikTok Advanced Matching data
 * TikTok pixel handles hashing automatically — pass plain-text values
 * 
 * @param email - User's email address (required)
 * @param userId - Optional user ID for external_id matching (improves EMQ)
 */
export function setTikTokAdvancedMatching(email: string, userId?: string): void {
    if (process.env.NODE_ENV !== "production") return
    if (typeof window === "undefined") return

    const ttq = (window as any).ttq
    if (!ttq) {
        console.warn("[TikTok Pixel] ttq not loaded, cannot set Advanced Matching")
        return
    }

    const normalizedEmail = email.toLowerCase().trim()
    if (!isValidTrackingEmail(normalizedEmail)) {
        console.warn("[TikTok Pixel] Invalid email, skipping Advanced Matching:", email ? "[non-empty invalid]" : "[empty]")
        return
    }

    try {
        const identifyData: Record<string, string> = {
            email: normalizedEmail
        }
        // Add external_id if userId provided (TikTok hashes automatically)
        if (userId) {
            identifyData.external_id = userId
        }
        ttq.identify(identifyData)

        // Persist for returning visitor identify on marketing pages
        try {
            const d = new Date()
            d.setTime(d.getTime() + 7776e6) // 90 days
            document.cookie = `_of_em=${encodeURIComponent(normalizedEmail)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`
            if (userId) {
                document.cookie = `_of_uid=${encodeURIComponent(userId)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`
            }
        } catch (e) {
            // Cookie write failure is non-critical
        }
    } catch (error) {
        console.error("[TikTok Pixel] Error setting Advanced Matching:", error)
    }
}

/**
 * Get TikTok attribution data from cookies
 * Returns ttp (browser ID) and ttclid (click ID)
 */
export function getTikTokAttributionData(): TikTokAttributionData {
    if (typeof document === "undefined") {
        return { ttp: null, ttclid: null }
    }

    const cookies = document.cookie.split(";").reduce(
        (acc, cookie) => {
            const [key, value] = cookie.trim().split("=")
            if (key) acc[key] = value || ""
            return acc
        },
        {} as Record<string, string>
    )

    const ttp = cookies["_ttp"] || null
    const ttclid = cookies["ttclid"] || null

    return { ttp, ttclid }
}
